/**
 * The company's own face on the login screen (U1, design §4.23).
 *
 * On a company's address the page shows that company's logo and name, with a
 * small "Powered by YardFlow" underneath; on the bare platform host it shows the
 * YardFlow wordmark as it always did. The name is what decides: `name: null`
 * means no company was resolved, so there is nothing to brand.
 *
 * No logo, or a logo that will not load, falls back to the name as a heading in
 * the logo's place, so a broken image never leaves the top of the page empty.
 */

import { useState } from 'react';

import { Wordmark } from '../../components/Logo';
import { brandMode } from './branding';

// Fades and rises in once. `motion-safe:` means reduced motion gets the finished
// state with no animation at all; keyframes live in index.css.
const ENTER = 'motion-safe:animate-[yf-brand-in_700ms_ease-out_both]';

export function CompanyBrand({
  name,
  logoUrl,
  loading = false,
}: {
  name: string | null;
  logoUrl: string | null;
  /** Still asking the server. Holds the logo's space so the form does not jump. */
  loading?: boolean;
}) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);

  if (loading) {
    return <div aria-hidden="true" className="h-[72px]" data-testid="brand-placeholder" />;
  }

  const mode = brandMode(name, logoUrl, failedUrl === logoUrl);

  if (mode === 'platform') {
    return (
      <div className="flex flex-col items-center text-center">
        {/* The mark is the page's heading, so it carries the h1 rather than
            sitting beside a duplicate one. */}
        <h1 className="flex">
          <Wordmark className="h-7 w-auto text-slate-900" />
        </h1>
        <p className="mt-2 text-sm text-slate-600">Yard inventory and gate control</p>
      </div>
    );
  }

  if (mode === 'name') {
    return (
      <div className={`flex flex-col items-center text-center ${ENTER}`}>
        <h1 className="text-3xl font-semibold text-slate-900">{name}</h1>
        <p className="mt-2 text-sm text-slate-600">Yard inventory and gate control</p>
      </div>
    );
  }

  return (
    <div className={`flex flex-col items-center text-center ${ENTER}`}>
      <img
        src={logoUrl ?? undefined}
        alt={name ?? ''}
        onError={() => setFailedUrl(logoUrl)}
        className="h-[72px] w-auto max-w-full object-contain"
      />
      <h1 className="mt-3 text-xl font-semibold text-slate-900">{name}</h1>
    </div>
  );
}

/** The small grey credit under the form. Absent on the platform host. */
export function PoweredBy({ className = 'mt-6' }: { className?: string }) {
  return (
    <p className={`${className} flex items-center justify-center gap-1.5 text-xs text-slate-500`}>
      <span>Powered by</span>
      <Wordmark className="h-3.5 w-auto" labelled={false} />
    </p>
  );
}
